'use client'

import { motion } from 'framer-motion'
import React, { useRef, useState, useEffect } from 'react'
import Link from 'next/link'
import LazyBackgroundDiv from './LazyBackgroundDiv'

type Option = 'basic' | 'pro' | 'premium' | 'basicPlus' | 'proPlus' | 'premiumPlus'

const barWidths: Record<Option, { bar1: string; bar2: string; bar3: string }> = {
  basic: { bar1: '43.5%', bar2: '31.5%', bar3: '23%' },
  pro: { bar1: '43.5%', bar2: '44%', bar3: '32%' },
  premium: { bar1: '68.5%', bar2: '57%', bar3: '43%' },
  basicPlus: { bar1: '94.5%', bar2: '95.5%', bar3: '64%' },
  proPlus: { bar1: '100%', bar2: '79%', bar3: '100%' },
  premiumPlus: { bar1: '100%', bar2: '96%', bar3: '100%' },
}

export default function PricingFrame() {
  // State controlled by click events
  const [selectedOption, setSelectedOption] = useState<Option | null>(null)

  // Ref for the section containing the cards and progress section
  const containerRef = useRef<HTMLDivElement>(null)

  // Helper function to compute a value between 1 and 15 from a given percentage.
  const computeValue = (barWidth: string): string => {
    const percentage = parseFloat(barWidth.replace('%', ''))
    const raw = 1 + (percentage / 100) * 14
    return raw.toFixed(1) // returns e.g. "4.5"
  }

  // Toggle selection when clicking on a card
  const handleCardClick = (option: Option) => {
    setSelectedOption((prev) => (prev === option ? null : option))
  }

  // Hide the progress section when clicking outside the container
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setSelectedOption(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  /**
   * StatsCard returns the stats markup.
   * On mobile (when `mobile` is true) it wraps the content in a container
   * limited to max-w-[400px] and centered.
   * On desktop (mobile false or omitted) it renders without the narrow constraint.
   */
  const StatsCard = (option: Option, mobile: boolean = false) => (
    <div className={`${mobile ? 'mx-auto w-full max-w-[400px]' : 'w-full'}`}>
      <div className="rounded-lg bg-white p-4 shadow-md">
        <h3 className="mb-2 text-lg font-semibold text-gray-800">Batteriekapazität</h3>
        <div className="flex items-center">
          <div className="relative h-3 flex-1 rounded-full bg-[#e9e9e6]">
            <div
              className="h-full rounded-full bg-[#ff4500] transition-all"
              style={{ width: barWidths[option].bar1 }}
            ></div>
          </div>
          <span className="ml-2 text-lg font-bold text-[#ff4500]">
            {computeValue(barWidths[option].bar1)} kWh
          </span>
        </div>
      </div>
      <div className="mt-4 rounded-lg bg-white p-4 shadow-md">
        <h3 className="mb-2 text-lg font-semibold text-gray-800">Energieerzeugung</h3>
        <div className="flex items-center">
          <div className="relative h-3 flex-1 rounded-full bg-[#e9e9e6]">
            <div
              className="h-full rounded-full bg-[#ff4500] transition-all"
              style={{ width: barWidths[option].bar2 }}
            ></div>
          </div>
          <span className="ml-2 text-lg font-bold text-[#ff4500]">
            {computeValue(barWidths[option].bar2)} kWp
          </span>
        </div>
      </div>
      <div className="mt-4 rounded-lg bg-white p-4 shadow-md">
        <h3 className="mb-2 text-lg font-semibold text-gray-800">Wechselrichterleistung</h3>
        <div className="flex items-center">
          <div className="relative h-3 flex-1 rounded-full bg-[#e9e9e6]">
            <div
              className="h-full rounded-full bg-[#ff4500] transition-all"
              style={{ width: barWidths[option].bar3 }}
            ></div>
          </div>
          <span className="ml-2 text-lg font-bold text-[#ff4500]">
            {computeValue(barWidths[option].bar3)} kW
          </span>
        </div>
      </div>
    </div>
  )

  return (
    <div>
      {/* Centering Container */}
      <div className="text-center md:pt-8" ref={containerRef}>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900 md:text-4xl">
          Unsere <span className="text-[#ff4500]">Volks-PV</span>-Pakete
        </h2>
        <p className="mt-8 px-4 text-center text-base text-gray-700 md:px-65 md:text-lg">
          Maximieren Sie Ihre Energieunabhängigkeit mit unserer hochwertigen und kostengünstigen
          Photovoltaikanlage. Effiziente Stromerzeugung kombiniert mit zuverlässiger
          Speichertechnologie für optimale Autarkie.
        </p>

        {/* Flex container for the cards */}
        <div
          className={`flex items-center justify-center pt-12 pb-12 md:pt-20 ${
            selectedOption ? 'md:pb-10' : 'md:pb-30'
          }`}
        >
          <div className="grid w-full max-w-7xl grid-cols-1 gap-4 gap-y-4 text-gray-800 antialiased md:grid-cols-4">
            {/* Card 1: Basic */}
            <div className="mx-auto">
              <motion.div
                onClick={() => handleCardClick('basic')}
                className="group relative mx-auto flex max-w-[400px] cursor-pointer flex-col rounded-xl bg-white shadow-lg transition-transform duration-300 ease-in-out hover:scale-105"
              >
                <LazyBackgroundDiv
                  src="/images/pricing-basic.webp"
                  className="relative z-10 flex aspect-[4/2] w-full items-center justify-center rounded-t bg-contain bg-center md:aspect-auto md:h-35 md:bg-cover"
                >
                  <div className="absolute inset-0 rounded-t bg-black opacity-2"></div>
                  {/* Added text overlay */}
                  <div className="relative z-20 text-center">
                    {/* <h3 className="text-3xl font-bold text-white">12.900€</h3> */}
                  </div>
                </LazyBackgroundDiv>
                <div className="relative z-10 mt-4 flex flex-1 flex-col items-center justify-center text-center hover:text-[#ff4500]">
                  <div className="mb-4">
                    <div className="text-xl font-bold">5.4 kWp + 7.1 kWh Speicher</div>
                  </div>
                  <div className="flex flex-col items-center text-left">
                    <span className="px-4 text-sm text-gray-700">
                      <p>• 12 x 450 Wp Glas Glas Bifazial Blackframe Module </p>
                      <p>• 7.1 kWh Deynees Batteriespeicher </p>
                      <p>• 4.2 kW Kostal Wechselrichter </p>
                      <p>• Unterkonstruktion </p>
                      <p>• E.on Home Box</p>
                      <p>• Installation + Inbetriebnahme</p>
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <Link href="/termin">
                    <button
                      onClick={(e) => e.stopPropagation()}
                      type="button"
                      className="w-full cursor-pointer rounded-lg bg-[#ff4500] py-2 font-bold text-white transition-colors hover:bg-[#e03e00]"
                    >
                      Jetzt anfragen
                    </button>
                  </Link>
                </div>
              </motion.div>
              {/* Mobile Inline Stats for Basic */}
              {selectedOption === 'basic' && (
                <div className="mt-4 md:hidden">{StatsCard('basic', true)}</div>
              )}
            </div>

            {/* Card 2: Pro */}
            <div className="mx-auto">
              <div
                onClick={() => handleCardClick('pro')}
                className="group relative mx-auto flex max-w-[400px] cursor-pointer flex-col rounded-xl bg-white shadow-lg transition-transform duration-300 ease-in-out hover:scale-105"
              >
                <LazyBackgroundDiv
                  src="/images/pricing-pro.webp"
                  className="relative z-10 flex aspect-[4/2] w-full items-center justify-center rounded-t bg-contain bg-center md:aspect-auto md:h-35 md:bg-cover"
                >
                  <div className="absolute inset-0 rounded-t bg-black opacity-2"></div>
                  {/* Added text overlay */}
                  <div className="relative z-20 text-center">
                    {/* <h3 className="text-3xl font-bold text-white">13.400€</h3> */}
                  </div>
                </LazyBackgroundDiv>
                <div className="mt-4 flex flex-1 flex-col items-center justify-center text-center text-black hover:text-[#ff4500]">
                  <div className="mb-4">
                    <div className="text-xl font-bold">7.2 kWp + 7.1 kWh Speicher</div>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="px-4 text-left text-sm text-gray-700">
                      <p> • 16 x 450 Wp Glas Glas Bifazial Blackframe Module</p>
                      <p>• 7.1 kWh Deynees Batteriespeicher</p>
                      <p>• 5.5 kW Kostal Wechselrichter</p>
                      <p>• Unterkonstruktion </p>
                      <p>• E.on Home Box</p>
                      <p>• Installation + Inbetriebnahme</p>
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <Link href="/termin">
                    <button
                      onClick={(e) => e.stopPropagation()}
                      type="button"
                      className="w-full cursor-pointer rounded-lg bg-[#ff4500] py-2 font-bold text-white transition-colors hover:bg-[#e03e00]"
                    >
                      Jetzt anfragen
                    </button>
                  </Link>
                </div>
              </div>
              {/* Mobile Inline Stats for Pro */}
              {selectedOption === 'pro' && (
                <div className="mt-4 md:hidden">{StatsCard('pro', true)}</div>
              )}
            </div>

            {/* Card 3: Premium */}
            <div className="mx-auto">
              <div
                onClick={() => handleCardClick('premium')}
                className="group mx-auto flex max-w-[400px] cursor-pointer flex-col rounded-xl bg-[#212121] shadow-lg transition-transform duration-300 ease-in-out hover:scale-105"
              >
                <LazyBackgroundDiv
                  src="/images/pricing-premium.webp"
                  className="relative z-10 flex aspect-[4/2] w-full items-center justify-center rounded-t bg-contain bg-center md:aspect-auto md:h-35 md:bg-cover"
                >
                  <div className="absolute top-2 left-2 z-50 rounded-full bg-[#ff4500] px-2 py-1 text-xs font-bold text-white">
                    Beliebt
                  </div>
                  <div className="absolute inset-0 rounded-t bg-black opacity-2"></div>
                  {/* Added text overlay */}
                  <div className="relative z-20 text-center">
                    {/* <h3 className="text-3xl font-bold text-white">13.900€</h3> */}
                  </div>
                </LazyBackgroundDiv>
                <div className="mt-4 flex flex-1 flex-col items-center justify-center text-center text-white hover:text-[#ff4500]">
                  <div className="mb-4">
                    <div className="text-xl font-bold">9 kWp + 10.66 kWh Speicher</div>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="px-4 text-left text-sm text-white">
                      <p>• 20 x 450 Wp Glas Glas Bifazial Blackframe Module</p>
                      <p>• 10.66 kWh Deynees Batteriespeicher</p>
                      <p>• 7.0 kW Kostal Wechselrichter</p>
                      <p>• Unterkonstruktion </p>
                      <p>• E.on Home Box</p>
                      <p>• Installation + Inbetriebnahme</p>
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <Link href="/termin">
                    <button
                      onClick={(e) => e.stopPropagation()}
                      type="button"
                      className="w-full cursor-pointer rounded-lg bg-[#ff4500] py-2 font-bold text-white transition-colors hover:bg-[#e03e00]"
                    >
                      Jetzt anfragen
                    </button>
                  </Link>
                </div>
              </div>
              {/* Mobile Inline Stats for Premium */}
              {selectedOption === 'premium' && (
                <div className="mt-4 md:hidden">{StatsCard('premium', true)}</div>
              )}
            </div>

            {/* Card 4: basicPlus */}
            <div className="mx-auto">
              <div
                onClick={() => handleCardClick('basicPlus')}
                className="group mx-auto flex max-w-[400px] cursor-pointer flex-col rounded-xl bg-white shadow-lg transition-transform duration-300 ease-in-out hover:scale-105"
              >
                <LazyBackgroundDiv
                  src="/images/pricing-basicplus.webp"
                  className="relative z-10 flex aspect-[4/2] w-full items-center justify-center rounded-t bg-contain bg-center md:aspect-auto md:h-35 md:bg-cover"
                >
                  <div className="absolute inset-0 rounded-t bg-black opacity-2"></div>
                  {/* Added text overlay */}
                  <div className="relative z-20 text-center">
                    {/* <h3 className="text-3xl font-bold text-white">18.900€</h3> */}
                  </div>
                </LazyBackgroundDiv>
                <div className="mt-4 flex flex-1 flex-col items-center justify-center text-center hover:text-[#ff4500]">
                  <div className="mb-4">
                    <div className="text-xl font-bold">14.4 kWp + 14.2 kWh Speicher</div>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="px-4 text-left text-sm text-gray-700">
                      <p>• 32 x 450 Wp Glas Glas Bifazial Blackframe Module</p>
                      <p>• 14.21 kWh Deynees Batteriespeicher</p>
                      <p>• 10 kW Kostal Wechselrichter</p>
                      <p>• Unterkonstruktion </p>
                      <p>• E.on Home Box</p>
                      <p>• Installation + Inbetriebnahme</p>
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <Link href="/termin">
                    <button
                      onClick={(e) => e.stopPropagation()}
                      type="button"
                      className="w-full cursor-pointer rounded-lg bg-[#ff4500] py-2 font-bold text-white transition-colors hover:bg-[#e03e00]"
                    >
                      Jetzt anfragen
                    </button>
                  </Link>
                </div>
              </div>
              {/* Mobile Inline Stats for basicPlus */}
              {selectedOption === 'basicPlus' && (
                <div className="mt-4 md:hidden">{StatsCard('basicPlus', true)}</div>
              )}
            </div>

            {/* Stats section for first row */}
            {selectedOption &&
              ['basic', 'pro', 'premium', 'basicPlus'].includes(selectedOption) && (
                <div className="col-span-4 mx-auto hidden w-full max-w-7xl md:block">
                  {StatsCard(selectedOption)}
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  )
}
